<template>
    <section class="section">
        <div class="container">
            <div class="columns">
                <div class="column">
                    <h1 class="title">
                        {{ translate("vouchers.title") }}
                    </h1>
                    <p>
                        {{ translate("vouchers.description") }}
                    </p>
                </div>
            </div>

            <div class="columns">
                <div class="column is-4">
                    <div v-if="addingEnabled">
                        <div class="field" :class="{ 'has-error': !numberOfFreeRegistrationsValid }">
                            <div class="control mb-2 mr-2">
                                <label class="label"> {{ translate("vouchers.number_of_free_registrations") }}: </label>
                                <input v-model="numberOfFreeRegistrations" class="input" type="text" />
                            </div>
                        </div>

                        <div class="buttons">
                            <LoadingButton
                                :is-loading="addingInProgress"
                                class="button is-success"
                                @click="confirmAdding"
                                :disabled="!canSubmitVoucher"
                            >
                                <span class="icon">
                                    <i class="fas fa-plus"></i>
                                </span>
                                <span>{{ translate("common.ok") }}</span>
                            </LoadingButton>
                            <button class="button is-danger" @click="disableAdding">
                                <span class="icon">
                                    <i class="fas fa-times"></i>
                                </span>
                                <span>{{ translate("common.cancel") }}</span>
                            </button>
                        </div>
                    </div>
                    <div class="buttons" v-else>
                        <button class="button is-primary" @click="enableAdding">
                            <span class="icon">
                                <i class="fas fa-plus"></i>
                            </span>
                            <span> {{ translate("vouchers.create_voucher") }} </span>
                        </button>
                    </div>
                </div>
            </div>

            <div class="columns">
                <div class="column">
                    <div class="box" v-for="voucher in vouchers" :key="voucher.id">
                        <div class="is-flex is-align-items-center is-flex-wrap-wrap">
                            <div class="is-flex-grow-1">
                                <h4 class="is-size-5 mb-2 is-uppercase voucher-code">
                                    <span class="icon">
                                        <i class="fas fa-ticket-alt"></i>
                                    </span>
                                    <span>
                                        {{ voucher.code }}
                                    </span>
                                </h4>
                                <p>
                                    {{ translate("common.created_at") }}:
                                    {{ autoConvertToTimezone(voucher.created) }}
                                </p>
                                <p>
                                    {{ translate("vouchers.usage_breakdown", false, [voucher.amountUsed, voucher.max]) }}
                                </p>
                                <p v-if="voucher.receiver">
                                    <span class="is-description">{{ translate("vouchers.used_by") }}</span>
                                    <span class="clarity-mask">
                                        {{ voucher.receiver.firstName }} {{ voucher.receiver.lastName }} ({{ voucher.receiver.email }})
                                    </span>
                                </p>
                            </div>
                            <div class="buttons pt-2">
                                <LoadingButton
                                    :is-loading="voucher.id === deleteInProgress"
                                    class="button is-small is-danger"
                                    @click="deleteVoucher(voucher.id)"
                                >
                                    <span class="icon">
                                        <i class="fas fa-trash"></i>
                                    </span>
                                    <span> {{ translate("common.delete") }} </span>
                                </LoadingButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref } from "vue";
    import vouchersApi, { FullVoucherData } from "../Api/vouchersApi";
    import { handleError } from "../Common/errorHandler";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { successToast } from "../Common/toasts";
    import { autoConvertToTimezone } from "../Common/dateTimeUtils";
    import { translate } from "../Common/translations";
    import { isIntegerGreaterThan0 } from "../Common/numbers";

    const props = defineProps<{ eventId: number }>();

    // LISTING

    const vouchers = ref<FullVoucherData[]>([]);

    const fetchVouchers = () => {
        return vouchersApi
            .getVouchersListing(props.eventId)
            .then(data => {
                vouchers.value = data;
            })
            .catch(handleError);
    };

    onMounted(() => {
        return fetchVouchers();
    });

    //ADDING

    const addingEnabled = ref(false);
    const numberOfFreeRegistrations = ref("");
    const enableAdding = () => {
        addingEnabled.value = true;
    };
    const disableAdding = () => {
        addingEnabled.value = false;
        numberOfFreeRegistrations.value = "";
    };
    const numberOfFreeRegistrationsValid = computed(() => {
        return numberOfFreeRegistrations.value === "" || isIntegerGreaterThan0(Number(numberOfFreeRegistrations.value));
    });

    const canSubmitVoucher = computed(() => {
        return numberOfFreeRegistrations.value !== "" && numberOfFreeRegistrationsValid.value;
    });
    const addingInProgress = ref(false);

    const confirmAdding = () => {
        const number = parseInt(numberOfFreeRegistrations.value, 10);
        if (isNaN(number) || number <= 0) {
            alert(translate("vouchers.invalid_number_of_free_registrations"));
            return;
        }

        addingInProgress.value = true;

        return vouchersApi
            .createVoucher({
                max: number,
                eventId: props.eventId,
            })
            .then(() => {
                disableAdding();
                successToast(translate("vouchers.created_successfully"));
                return fetchVouchers();
            })
            .catch(handleError)
            .finally(() => {
                addingInProgress.value = false;
            });
    };

    //DELETE

    const deleteInProgress = ref(0);

    const deleteVoucher = (voucherId: number) => {
        if (!confirm(translate("common.are_you_sure"))) {
            return;
        }

        deleteInProgress.value = voucherId;

        return vouchersApi
            .deleteVoucher(props.eventId, voucherId)
            .then(() => {
                successToast(translate("vouchers.voucher_deleted"));
                return fetchVouchers();
            })
            .catch(handleError)
            .finally(() => {
                deleteInProgress.value = 0;
            });
    };
</script>

<style scoped></style>
